<script setup lang="ts">
import { adminUpdateJobImportItem, useAdminGetJobImportBatch } from '@swipe4work/api-orval'
import { type GetJobImportBatchItemDto, JobImportStatus } from '@swipe4work/models-orval'
import { useJobImportRunner } from '@/features/jobs-import/composables/useJobImportRunner'
import JobEditForm from '@/shared/components/job/JobEditForm.vue'
import { useToast } from '@/shared/composables/UseToast'

definePage({
  path: '/settings/jobs-import/:batchId',
  name: '/settings/JobsImportFinalize',
  meta: {
    title: 'Vacature-import afronden',
    permission: {
      resource: 'Job',
      level: 'Read',
    },
  },
})

const route = useRoute()
const toast = useToast()

const batchId = computed(() => Number((route.params as { batchId?: string }).batchId))

const { data: batch, isPending, refetch } = useAdminGetJobImportBatch(batchId, {
  query: { staleTime: 0 },
})

const items = computed<GetJobImportBatchItemDto[]>(() => batch.value?.items ?? [])

const runner = useJobImportRunner({
  onItemChanged: async () => {
    await refetch()
  },
})

// Kick off (and resume) conversion once the batch is loaded. Runner guards against double-starts.
const started = ref(false)

watch(items, (list) => {
  if (!started.value && list.length > 0) {
    started.value = true
    runner.run(list)
  }
}, { immediate: true })

// Poll the batch while anything is still converting, so drafts appear as they complete.
let pollTimer: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  pollTimer = setInterval(() => {
    if (items.value.some(i => i.status === JobImportStatus.Converting || i.status === JobImportStatus.PendingConversion)) {
      refetch()
    }
  }, 4000)
})
onBeforeUnmount(() => {
  if (pollTimer) clearInterval(pollTimer)
})

// ── Active item navigation ──────────────────────────────────────────────────
const activeIndex = ref(0)
const activeItem = computed(() => items.value[activeIndex.value] ?? null)

function goToPrevious() {
  if (activeIndex.value > 0) activeIndex.value--
}
function goToNext() {
  if (activeIndex.value < items.value.length - 1) activeIndex.value++
}

const statusMeta: Record<JobImportStatus, {
  label: string,
  severity: string,
  icon: string 
}> = {
  [JobImportStatus.PendingConversion]: {
    label: 'In wachtrij',
    severity: 'secondary',
    icon: 'fa-regular fa-clock', 
  },
  [JobImportStatus.Converting]: {
    label: 'AI verwerkt...',
    severity: 'info',
    icon: 'fa-regular fa-spinner fa-spin', 
  },
  [JobImportStatus.DraftCreated]: {
    label: 'Concept klaar',
    severity: 'warn',
    icon: 'fa-regular fa-pen-to-square', 
  },
  [JobImportStatus.Finalized]: {
    label: 'Afgerond',
    severity: 'success',
    icon: 'fa-regular fa-check', 
  },
  [JobImportStatus.Failed]: {
    label: 'Mislukt',
    severity: 'danger',
    icon: 'fa-regular fa-triangle-exclamation', 
  },
}

const doneCount = computed(() => items.value.filter(i => i.status === JobImportStatus.Finalized).length)

async function onJobSaved() {
  if (!activeItem.value) return
  // Persisting via UpdateAdminJob already saved the job; mark the import item finalized for tracing.
  await adminUpdateJobImportItem(activeItem.value.id, { status: JobImportStatus.Finalized })
  await refetch()
  toast.success('Vacature afgerond', 'Ga verder met de volgende vacature.')
  goToNext()
}

function resumeConversion() {
  // Explicit user action → also retry failed items.
  runner.run(items.value, true)
}

async function regenerateActive() {
  const item = activeItem.value
  if (!item) return
  await runner.regenerate(item)
  await refetch()
}
</script>

<template>
  <div class="flex flex-col gap-6 p-6">
    <div v-if="isPending" class="flex flex-col gap-2">
      <Skeleton v-for="n in 4" :key="n" height="3rem" />
    </div>

    <template v-else-if="batch">
      <!-- Header -->
      <div class="flex items-center justify-between">
        <div class="flex flex-col gap-1">
          <h2 class="m-0">
            {{ batch.name || 'Vacature-import' }}
          </h2>
          <span class="text-sm text-surface-500">
            {{ batch.companyName }} — {{ doneCount }}/{{ items.length }} afgerond
          </span>
        </div>
        <Button
          v-if="items.some(i => i.status === JobImportStatus.PendingConversion || i.status === JobImportStatus.Failed)"
          label="Hervat conversie"
          icon="fa-regular fa-rotate-right"
          severity="secondary"
          size="small"
          :loading="runner.isRunning.value"
          @click="resumeConversion"
        />
      </div>

      <div class="flex flex-col gap-6 lg:flex-row">
        <!-- Left: item list / stepper -->
        <div class="flex flex-col gap-2 lg:w-72 lg:shrink-0">
          <button
            v-for="(item, index) in items"
            :key="item.id"
            type="button"
            class="flex items-center gap-3 border rounded-lg px-3 py-2 text-left transition-colors"
            :class="index === activeIndex ? 'border-primary-500 bg-primary-50' : 'border-surface-200 hover:bg-surface-50'"
            @click="activeIndex = index"
          >
            <i :class="statusMeta[item.status].icon" class="w-4 text-center text-sm text-surface-500" />
            <div class="min-w-0 flex flex-1 flex-col">
              <span class="truncate text-sm font-medium">
                {{ item.jobTitle || `Vacature ${index + 1}` }}
              </span>
              <Tag :value="statusMeta[item.status].label" :severity="statusMeta[item.status].severity" class="mt-1 w-fit text-xs" />
            </div>
          </button>
        </div>

        <!-- Right: active item detail -->
        <div class="min-w-0 flex-1">
          <Card v-if="activeItem">
            <template #content>
              <!-- Draft ready → embed the full edit form -->
              <template v-if="activeItem.status === JobImportStatus.DraftCreated || activeItem.status === JobImportStatus.Finalized">
                <div class="mb-3 flex items-center justify-between gap-3 border border-surface-200 rounded-lg bg-surface-50 px-3 py-2">
                  <span class="text-sm text-surface-600">
                    Niet tevreden met het gegenereerde concept? Laat de AI het opnieuw genereren.
                  </span>
                  <Button
                    label="Opnieuw genereren"
                    icon="fa-regular fa-wand-magic-sparkles"
                    severity="secondary"
                    size="small"
                    :loading="runner.isRunning.value"
                    @click="regenerateActive"
                  />
                </div>
                <JobEditForm
                  :key="activeItem.id"
                  :job-id="activeItem.jobId!"
                  :show-heading="false"
                  :show-cancel="false"
                  save-label="Opslaan & afronden"
                  @saved="onJobSaved"
                />
              </template>

              <!-- Failed -->
              <div v-else-if="activeItem.status === JobImportStatus.Failed" class="flex flex-col items-center gap-3 py-10 text-center">
                <i class="fa-regular fa-triangle-exclamation text-3xl text-red-500" />
                <p class="m-0 font-medium">
                  Conversie mislukt
                </p>
                <p class="m-0 max-w-md text-sm text-surface-500">
                  {{ activeItem.errorMessage }}
                </p>
                <Button label="Opnieuw proberen" icon="fa-regular fa-rotate-right" size="small" @click="resumeConversion" />
              </div>

              <!-- Still converting / pending -->
              <div v-else class="flex flex-col items-center gap-3 py-10 text-center">
                <i :class="statusMeta[activeItem.status].icon" class="text-3xl text-primary-500" />
                <p class="m-0 font-medium">
                  {{ statusMeta[activeItem.status].label }}
                </p>
                <p class="m-0 max-w-md text-sm text-surface-500">
                  De AI verwerkt deze vacature. Zodra het concept klaar is verschijnt hier het bewerkformulier.
                </p>
              </div>
            </template>
          </Card>

          <!-- Navigation -->
          <div class="mt-4 flex items-center justify-between">
            <Button
              label="Vorige"
              icon="fa-regular fa-chevron-left"
              severity="secondary"
              :disabled="activeIndex === 0"
              @click="goToPrevious"
            />
            <span class="text-sm text-surface-500">{{ activeIndex + 1 }} / {{ items.length }}</span>
            <Button
              label="Volgende"
              icon="fa-regular fa-chevron-right"
              icon-pos="right"
              severity="secondary"
              :disabled="activeIndex === items.length - 1"
              @click="goToNext"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
